import { strFromU8, unzip } from 'fflate'

const BUILT_IN_PERCENT_FORMATS = {
  9: '0%',
  10: '0.00%'
}

function readArchive (file) {
  return file.arrayBuffer().then(arrayBuffer => new Promise((resolve, reject) => {
    unzip(new Uint8Array(arrayBuffer), {
      filter: entry => (
        entry.name === 'xl/workbook.xml' ||
        entry.name === 'xl/_rels/workbook.xml.rels' ||
        /(^|\/)styles\.xml$/i.test(entry.name) ||
        /(^|\/)worksheets\/[^/]+\.xml$/i.test(entry.name)
      )
    }, (error, files) => {
      if (error) {
        reject(error)
        return
      }

      resolve(files)
    })
  }))
}

function parseXml (archive, path) {
  const file = archive[path]

  if (!file) {
    throw new Error(`Excel 文件缺少 ${path}`)
  }

  const document = new DOMParser().parseFromString(strFromU8(file), 'application/xml')

  if (document.getElementsByTagName('parsererror').length > 0) {
    throw new Error(`Excel 文件中的 ${path} 无法解析`)
  }

  return document
}

function getElements (document, localName) {
  return Array.from(document.getElementsByTagNameNS('*', localName))
}

function getChildElements (element, localName) {
  return Array.from(element.childNodes).filter(
    child => child.nodeType === 1 && child.localName === localName
  )
}

function resolveArchivePath (sourcePath, target) {
  target = target.replace(/\\/g, '/')

  if (target.startsWith('/')) {
    return target.slice(1)
  }

  const parts = sourcePath.split('/')
  parts.pop()

  target.split('/').forEach(part => {
    if (!part || part === '.') {
      return
    }

    if (part === '..') {
      parts.pop()
      return
    }

    parts.push(part)
  })

  return parts.join('/')
}

function getWorkbookPaths (archive) {
  const workbookPath = 'xl/workbook.xml'
  const workbook = parseXml(archive, workbookPath)
  const relationships = parseXml(archive, 'xl/_rels/workbook.xml.rels')
  const firstSheet = getElements(workbook, 'sheet')[0]

  if (!firstSheet) {
    throw new Error('Excel 中没有工作表')
  }

  const sheetRelationshipId = firstSheet.getAttributeNS(
    'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
    'id'
  ) || firstSheet.getAttribute('r:id')
  const relationshipElements = getElements(relationships, 'Relationship')
  const sheetRelationship = relationshipElements.find(
    relationship => relationship.getAttribute('Id') === sheetRelationshipId
  )
  const stylesRelationship = relationshipElements.find(
    relationship => /\/styles$/i.test(relationship.getAttribute('Type') || '')
  )

  if (!sheetRelationship) {
    throw new Error('无法定位 Excel 的首个工作表')
  }

  return {
    sheetPath: resolveArchivePath(workbookPath, sheetRelationship.getAttribute('Target')),
    stylesPath: stylesRelationship
      ? resolveArchivePath(workbookPath, stylesRelationship.getAttribute('Target'))
      : null
  }
}

function getPercentageFormat (formatCode) {
  const section = formatCode.split(';')[0]
  let normalized = ''
  let quoted = false
  let bracketed = false

  for (let index = 0; index < section.length; index += 1) {
    const character = section[index]

    if (character === '"') {
      quoted = !quoted
      continue
    }

    if (quoted) {
      continue
    }

    if (character === '[') {
      bracketed = true
      continue
    }

    if (character === ']') {
      bracketed = false
      continue
    }

    if (bracketed) {
      continue
    }

    if (character === '\\' || character === '_' || character === '*') {
      index += 1
      continue
    }

    normalized += character
  }

  const percentIndex = normalized.indexOf('%')

  if (percentIndex === -1) {
    return null
  }

  const numberPattern = normalized.slice(0, percentIndex)
  const decimalIndex = numberPattern.lastIndexOf('.')
  const decimalPattern = decimalIndex === -1
    ? ''
    : numberPattern.slice(decimalIndex + 1).replace(/[^0#?]/g, '')

  return {
    minimumFractionDigits: (decimalPattern.match(/0/g) || []).length,
    maximumFractionDigits: decimalPattern.length
  }
}

export function formatPercentage (value, format) {
  const maximumFractionDigits = Math.min(format.maximumFractionDigits, 20)
  const minimumFractionDigits = Math.min(format.minimumFractionDigits, maximumFractionDigits)
  let text = (value * 100).toFixed(maximumFractionDigits)

  if (maximumFractionDigits > minimumFractionDigits) {
    const parts = text.split('.')
    let fraction = parts[1]

    while (fraction.length > minimumFractionDigits && fraction.endsWith('0')) {
      fraction = fraction.slice(0, -1)
    }

    text = fraction ? `${parts[0]}.${fraction}` : parts[0]
  }

  return `${text}%`
}

function getStyleFormats (stylesDocument) {
  if (!stylesDocument) {
    return []
  }

  const customFormats = getElements(stylesDocument, 'numFmt').reduce((formats, element) => {
    formats[element.getAttribute('numFmtId')] = element.getAttribute('formatCode')
    return formats
  }, {})
  const cellFormats = getElements(stylesDocument, 'cellXfs')[0]

  if (!cellFormats) {
    return []
  }

  return getChildElements(cellFormats, 'xf').map(element => {
    const numberFormatId = element.getAttribute('numFmtId')
    const formatCode = customFormats[numberFormatId] || BUILT_IN_PERCENT_FORMATS[numberFormatId]
    return formatCode ? getPercentageFormat(formatCode) : null
  })
}

function parseCellAddress (address) {
  const match = /^([A-Z]+)(\d+)$/i.exec(address)

  if (!match) {
    return null
  }

  const column = match[1].toUpperCase().split('').reduce(
    (number, character) => number * 26 + character.charCodeAt(0) - 64,
    0
  )

  return {
    rowIndex: Number(match[2]) - 1,
    columnIndex: column - 1
  }
}

export async function readPercentageCellOverrides (file) {
  const archive = await readArchive(file)
  const { sheetPath, stylesPath } = getWorkbookPaths(archive)
  const sheet = parseXml(archive, sheetPath)
  const styles = stylesPath && archive[stylesPath] ? parseXml(archive, stylesPath) : null
  const styleFormats = getStyleFormats(styles)

  return getElements(sheet, 'c').reduce((overrides, cell) => {
    const format = styleFormats[Number(cell.getAttribute('s'))]
    const type = cell.getAttribute('t') || 'n'

    if (!format || type !== 'n') {
      return overrides
    }

    const valueElement = getChildElements(cell, 'v')[0]
    const address = parseCellAddress(cell.getAttribute('r'))
    const value = valueElement ? Number(valueElement.textContent) : NaN

    if (address && Number.isFinite(value)) {
      overrides.push({
        ...address,
        text: formatPercentage(value, format)
      })
    }

    return overrides
  }, [])
}
